import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, AlertTriangle, Trash2, Sparkles, Clock, CheckCircle2, Pause } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  trial: { label: "Trial", icon: Sparkles, color: "bg-blue-100 text-blue-800 border-blue-300" },
  trial_expired: { label: "Trial expired", icon: AlertTriangle, color: "bg-amber-100 text-amber-900 border-amber-300" },
  active: { label: "Active", icon: CheckCircle2, color: "bg-green-100 text-green-800 border-green-300" },
  renewal_due: { label: "Renewal due", icon: Clock, color: "bg-purple-100 text-purple-800 border-purple-300" },
  pending_approval: { label: "Pending", icon: Loader2, color: "bg-orange-100 text-orange-800 border-orange-300" },
  suspended: { label: "Suspended", icon: Pause, color: "bg-red-100 text-red-700 border-red-300" },
  archived: { label: "Archived", icon: Trash2, color: "bg-gray-100 text-gray-700 border-gray-300" },
  all: { label: "All", icon: null, color: "bg-white text-[hsl(var(--kb-text))] border-[hsl(var(--kb-border))]" },
};

const ORDER = ["all", "trial", "active", "renewal_due", "trial_expired", "pending_approval", "suspended", "archived"];

/**
 * Status overview pills + live tenant list for super-admin.
 * - Loads /api/superadmin/tenants/stats for the counts.
 * - Clicking a pill loads /api/superadmin/tenants-by-status?status=<s>.
 * - Inline "Delete" button hits /api/superadmin/tenants/{id}/hard-delete.
 */
export default function TenantStatusBoard() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("trial");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const refreshAll = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/superadmin/tenants/stats"),
        api.get(`/superadmin/tenants-by-status?status=${status}`),
      ]);
      setStats(s.data);
      setRows(t.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tenant stats");
    }
  };

  useEffect(() => { refreshAll(); /* eslint-disable-next-line */ }, [status]);

  const onDelete = async (t) => {
    if (!window.confirm(`Permanently delete "${t.name}" and ALL its data?\nThis cannot be undone.`)) return;
    setBusy(`delete-${t.id}`);
    try {
      await api.delete(`/superadmin/tenants/${t.id}/hard-delete`);
      toast.success("Tenant deleted");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  if (!stats) {
    return <div className="text-xs text-[hsl(var(--kb-muted))] py-4"><Loader2 size={14} className="animate-spin inline mr-2" />Loading status…</div>;
  }

  return (
    <section className="space-y-4" data-testid="tenant-status-board">
      <div>
        <h2 className="font-display text-xl mb-2">Lifecycle overview</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
          Click a tile to drill into that bucket. Renewal-due = paid subscriptions whose billing cycle ends in the next {stats.renewal_window_days} days.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="status-tiles">
        {ORDER.map((key) => {
          const meta = STATUS_LABELS[key];
          const count = key === "all" ? stats.total : stats[key];
          const active = status === key;
          const Icon = meta.icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setStatus(key)}
              className={`text-left border px-3 py-2 transition-colors ${meta.color} ${active ? "ring-2 ring-[hsl(var(--kb-primary))] ring-offset-1" : "opacity-80 hover:opacity-100"}`}
              data-testid={`status-tile-${key}`}
            >
              <div className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                {Icon && <Icon size={10} />}
                {meta.label}
              </div>
              <div className="font-display text-2xl mt-0.5" data-testid={`status-count-${key}`}>{count ?? 0}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="px-4 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between">
          <div className="text-sm">
            <span className="font-medium">{STATUS_LABELS[status].label}</span>
            <span className="text-[hsl(var(--kb-muted))] ml-2">({rows.length} tenant{rows.length === 1 ? "" : "s"})</span>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="text-xs text-[hsl(var(--kb-muted))] p-6 text-center" data-testid="status-rows-empty">No tenants in this state.</div>
        ) : (
          <div className="divide-y divide-[hsl(var(--kb-border))]" data-testid="status-rows">
            {rows.map((t) => (
              <div key={t.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`status-row-${t.slug}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{t.name || t.slug}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                    /t/{t.slug}/ · {t.owner_phone || "—"}
                    {t.trial_ends_at && (status === "trial" || status === "trial_expired") && (
                      <> · trial ends {new Date(t.trial_ends_at).toLocaleDateString()}</>
                    )}
                    {t.cycle_end_date && status === "renewal_due" && (
                      <> · renews {new Date(t.cycle_end_date).toLocaleDateString()}</>
                    )}
                    {t.active_plan_slug && (
                      <> · plan <b>{t.active_plan_slug}</b></>
                    )}
                  </div>
                </div>
                <Link
                  to={`/t/${t.slug}/`}
                  target="_blank"
                  className="kb-btn-outline text-xs"
                  data-testid={`status-view-${t.slug}`}
                >
                  View store
                </Link>
                {t.slug !== "default" && (
                  <button
                    type="button"
                    disabled={busy === `delete-${t.id}`}
                    onClick={() => onDelete(t)}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                    data-testid={`status-delete-${t.slug}`}
                  >
                    {busy === `delete-${t.id}` ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
